import axios from "axios";
import { REACT_APP_API_BASE_URL } from "../Constants/APIConstants";

if (!REACT_APP_API_BASE_URL) {
  // eslint-disable-next-line no-console
  console.error("REACT_APP_API_BASE_URL is not defined. Please check your .env.development file.");
}

const apiClient = axios.create({
  baseURL: REACT_APP_API_BASE_URL || "",
  timeout: 10000,
  headers: {
    "Cache-Control": "no-cache",
    Accept: "application/json",
  },
});

// Add request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    // Only log in development to reduce console noise
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("API Request:", config.method?.toUpperCase(), config.baseURL + config.url);
    }
    return config;
  },
  (error) => {
    // eslint-disable-next-line no-console
    console.error("API Request Error:", error);
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Check if response is HTML instead of JSON (Typo3 error page)
    const contentType = response.headers["content-type"] || "";
    if (contentType.includes("text/html") && !contentType.includes("application/json")) {
      // eslint-disable-next-line no-console
      console.error(
        "Received HTML instead of JSON. Typo3 backend may not be configured for headless mode.",
        {
          url: response.config?.url,
          baseURL: response.config?.baseURL,
          contentType,
        },
      );
      return Promise.reject(
        new Error(
          "Backend returned HTML instead of JSON. Please check Typo3 headless configuration.",
        ),
      );
    }
    return response;
  },
  (error) => {
    // eslint-disable-next-line no-console
    console.error("API Error:", {
      message: error.message,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      status: error.response?.status,
      statusText: error.response?.statusText,
      contentType: error.response?.headers?.["content-type"],
      code: error.code,
    });

    // Handle network errors (CORS, SSL, connectivity issues)
    if (
      error.message === "Network Error" ||
      error.code === "ERR_NETWORK" ||
      error.code === "ECONNABORTED" ||
      (!error.response && error.request)
    ) {
      const fullUrl = error.config?.baseURL + error.config?.url;
      // eslint-disable-next-line no-console
      console.error(
        "Network Error Detected - Possible causes:",
        "\n1. CORS: The backend may not allow requests from this origin.",
        "\n   - Check if CORS headers are configured on the backend",
        "\n   - For ddev sites, ensure CORS is enabled in Typo3 configuration",
        "\n2. SSL Certificate: If using HTTPS with self-signed certificates (common with ddev),",
        "\n   - The browser may be blocking the request",
        "\n   - Try accessing the URL directly in the browser to accept the certificate",
        "\n3. Server not reachable:",
        "\n   - Verify the backend is running:",
        `\n   - Check: ${fullUrl}`,
        "\n4. Development proxy: Consider using a proxy in package.json to avoid CORS issues",
        "\n\nFull URL attempted:",
        fullUrl,
      );
    }

    // If we get HTML error pages, provide more helpful error message
    if (error.response?.headers?.["content-type"]?.includes("text/html")) {
      // eslint-disable-next-line no-console
      console.error(
        "Typo3 backend is returning HTML error pages. Ensure:",
        "\n1. EXT:headless is installed and configured",
        "\n2. Site configuration has headless: true",
        "\n3. CORS is properly configured",
        "\n4. The API endpoint is correct",
      );
    }

    return Promise.reject(error);
  },
);

export default apiClient;
